import { format } from 'date-fns'
import {
  flatten,
  has,
  isArray,
  isBoolean,
  isNumber,
  isString,
  map,
  mapValues,
  pick,
} from 'lodash-es'

import { EMPTY_LIST, EMPTY_OBJECT, EMPTY_STRING } from '../constants/common'
import { DATE_API_DATA_FORMAT, DATE_SHORT_FORMAT } from '../constants/date'
import {
  DWELLER_ENTITY,
  INHABITANT_ENTITY,
  LANDLORD_ENTITY,
  PROVIDER_ENTITY,
} from '../constants/entity'
import {
  AREA_FILTER_NAME,
  CUSTOM_FILTER_TYPE,
  DATE_FILTER_TYPE,
  DATE_FILTER_TYPES,
  DATE_HIDDEN_TIME_FILTER_TYPE,
  FILE_FILTER_LIST,
  FLOAT_LIST_TYPE,
  HIDDEN_FILTER_TYPE,
  LABEL_LIST_TYPE,
  LIVE_AREA_FILTER_NAME,
  MULTISELECT_FILTER_TYPE,
  NUMBER_RANGE_FILTER_TYPE,
  RATING_FILTER_NAME,
  RATING_LIST_TYPE,
  SELECT_FILTER_TYPE,
  STATUS_LIST_TYPE,
  TOGGLE_FILTER_TYPE,
  USER_FILTER_LIST,
  USER_LIST_TYPE,
} from '../constants/filter'

import { getMassActionExcludeIdParam, getMassActionIdParam } from './common'
import { toUkTimezoneDate } from './date'
import { getDocFlowResultStatusFilterOption } from './docFlow'
import { isSubArchive, isSubRead, isSubUnread } from './route'

export function isToggleFilterType(type) {
  return type === TOGGLE_FILTER_TYPE
}

export function isDateFilterType(type) {
  return DATE_FILTER_TYPES.includes(type)
}

export function isNumberRangeFilterType(type) {
  return type === NUMBER_RANGE_FILTER_TYPE
}

export function isMultiselectFilterType(type) {
  return type === MULTISELECT_FILTER_TYPE
}

export function isSelectFilterType(type) {
  return type === SELECT_FILTER_TYPE
}

export function isCustomFilterType(type) {
  return type === CUSTOM_FILTER_TYPE
}

export function isFloatListType(type) {
  return type === FLOAT_LIST_TYPE
}

export function isRatingFilterName(name) {
  return name === RATING_FILTER_NAME
}

export function isRangeFilterType(type) {
  return isDateFilterType(type) || isNumberRangeFilterType(type)
}

// Scoped tables (building group inhabitants, building contractors) share the
// user-shaped `id` filter of their base entity, so compare by `single`.
export function isFilterNameIdEntity(entity) {
  return (
    entity?.single === INHABITANT_ENTITY.single ||
    entity?.single === PROVIDER_ENTITY.single
  )
}

export function getListType({ name, type }, entity) {
  if (
    USER_FILTER_LIST.includes(name) ||
    (name === 'id' && isFilterNameIdEntity(entity))
  ) {
    return USER_LIST_TYPE
  }

  if (name === RATING_FILTER_NAME) {
    return RATING_LIST_TYPE
  }

  if (name === 'label') {
    return LABEL_LIST_TYPE
  }

  if (name === 'status') {
    return STATUS_LIST_TYPE
  }

  if (name === 'role') {
    return 'role'
  }

  if (FILE_FILTER_LIST.includes(name)) {
    return 'attachment'
  }

  if (name === 'linked_request' || name === 'request') {
    return 'request'
  }

  if (name.includes('_range')) {
    return 'range'
  }

  if (type === DATE_FILTER_TYPE || type === DATE_HIDDEN_TIME_FILTER_TYPE) {
    return DATE_FILTER_TYPE
  }

  if (name === AREA_FILTER_NAME || name === LIVE_AREA_FILTER_NAME) {
    return FLOAT_LIST_TYPE
  }
}

export function getDateFieldsList(availableFilters) {
  const dateFilters = availableFilters.filter(
    ({ type }) =>
      type === DATE_FILTER_TYPE || type === DATE_HIDDEN_TIME_FILTER_TYPE
  )

  return flatten(
    dateFilters.map(({ name }) => [
      getDateStartParam(name),
      getDateEndParam(name),
    ])
  )
}

// Hidden-type filters (e.g. the scope of an entity tab) never render, so a list
// made of them alone offers the user nothing to filter by.
export function getVisibleFilters(availableFilters) {
  return availableFilters.filter(({ type }) => type !== HIDDEN_FILTER_TYPE)
}

export function getSelectTypeOptions(currentFilters, availableFilters) {
  const selectFieldList = availableFilters
    .filter(({ type }) => type === SELECT_FILTER_TYPE)
    .map(({ name }) => name)

  return pick(currentFilters, selectFieldList)
}

export function getDateOptions({ availableFilters, currentFilters, timezone }) {
  const dateFieldsList = getDateFieldsList(availableFilters)

  const dateValues = pick(currentFilters, dateFieldsList)

  return mapValues(dateValues, (apiValue, key) => {
    const ukDate = toUkTimezoneDate(apiValue, timezone)

    return {
      key,
      label: format(ukDate, DATE_SHORT_FORMAT),
      value: format(ukDate, DATE_API_DATA_FORMAT),
    }
  })
}

export function getValuesFromOptions(options) {
  return mapValues(options, option => {
    if (isString(option) || isNumber(option)) {
      return option
    }

    if (isArray(option) && option.length === 0) {
      return undefined
    }

    if (isBoolean(option)) {
      return option
    }

    if (has(option, 'value')) {
      return option.value
    }

    return map(option, 'value')
  })
}

export function getDateStartParam(param) {
  const cleanParam = getCleanDateParam(param)

  return `${cleanParam}_gte`
}

export function getDateEndParam(param) {
  const cleanParam = getCleanDateParam(param)

  return `${cleanParam}_lte`
}

export function getDateRangeParam(param) {
  const cleanParam = getCleanDateParam(param)

  return `${cleanParam}_range`
}

export function getCleanDateParam(param) {
  return param.replace(/(_gte|_lte|_range)$/, EMPTY_STRING)
}

export function getFloatLabel(option) {
  if (option.value && !option.is_persistent) {
    return option.label.replace('.', ',')
  }

  return option.label
}

export function getDocFlowResultStatusLabelGetter(name) {
  return ({ label, value }) => {
    const { statuses, statusId } = getDocFlowResultStatusFilterOption({
      name,
      value,
    })

    return {
      isPersistent: true,
      label: statuses[statusId]?.text || label,
    }
  }
}

export function getRelatedFilters(availableFilters) {
  const regexp = /_range$/

  const masterFilters = availableFilters.reduce((acc, { name }) => {
    if (regexp.test(name)) {
      acc[name] = null
    }

    return acc
  }, {})

  const slaveFilters = availableFilters.reduce((acc, { name }) => {
    if (name.includes('_range')) {
      return acc
    }

    const dateRangeName = getDateRangeParam(name)

    if (has(masterFilters, dateRangeName)) {
      acc[dateRangeName] = name
    }

    return acc
  }, {})

  return slaveFilters
}

export function getEmptyPermanentFilters() {
  return EMPTY_OBJECT
}

export function getPermanentFilters({ entityType, sub }) {
  if (!sub) {
    return {}
  }

  if (isSubUnread(sub)) {
    return { archived: false, unread: true }
  }

  if (isSubRead(sub)) {
    return { archived: false, unread: false }
  }

  if (isSubArchive(sub)) {
    return { archived: true }
  }

  if (entityType === INHABITANT_ENTITY.plural) {
    if (sub === DWELLER_ENTITY.plural) {
      return { archived: false, role: [DWELLER_ENTITY.single] }
    }

    if (sub === LANDLORD_ENTITY.plural) {
      return { archived: false, role: [LANDLORD_ENTITY.single] }
    }
  }

  return { archived: false }
}

export function getIdFilterParam({
  filterKey = 'id',
  filterValues,
  ids,
  selectAllPages,
}) {
  const filterIds = filterValues?.[filterKey]

  if (selectAllPages) {
    if (filterIds?.length > 0) {
      return filterIds
    } else {
      return ids
    }
  }

  return ids?.length > 0 ? ids : filterIds?.length > 0 ? filterIds : EMPTY_LIST
}

export function getApiRequestFilterParams({
  excludeIds,
  filterValues,
  ids,
  massActionIdKey = 'id',
  selectAllPages,
  selectedIds,
  sub,
}) {
  const id = getMassActionIdParam({ ids, selectAllPages, selectedIds })
  const exclude_id = getMassActionExcludeIdParam({
    excludeIds,
    selectAllPages,
  })

  return {
    filters: {
      ...filterValues,
      ...getPermanentFilters({ sub }),
      exclude_id,
      [massActionIdKey]: getIdFilterParam({
        filterValues,
        ids: id,
        selectAllPages,
      }), // needs to prevent action with related request
    },
    id,
  }
}
